/**
 * Decisions for the phone's live transcript panel.
 *
 * Pure, like `call-control.ts`, because the rules here are ones the stream does not enforce and
 * the widget must not improvise:
 *
 *  - **One partial per speaker.** A partial is the model's current guess at a turn still being
 *    spoken; the next one replaces it, and the final retires it. Both parties talk at once on a
 *    real call, so the guesses are kept per role — appended instead, the panel would fill with
 *    superseded text faster than anyone could read it.
 *  - **Lines are filtered by call.** The stream is already call-scoped server-side; checking
 *    `callId` again here is what keeps one stale handle from bleeding a previous call's lines
 *    into the next call's panel during the toggle-off/toggle-on shuffle.
 *
 * Adapted from tap-ucaas `web/components/softphone/transcription-widget.tsx`
 * (`useCallTranscription`'s reducer half), with this platform's speaker vocabulary — the
 * service tags lines `rep` / `lead` / `unknown`, not `local` / `remote`.
 */

/** One transcript line as the panel renders it. */
export interface TranscriptLine {
  key: string;
  /** rep | lead | unknown — which stereo channel the audio arrived on. */
  role: string;
  /** Display label resolved from the call's own party info; null renders as no attribution. */
  label: string | null;
  text: string;
  /** Wall-clock HH:MM:SS, finals only — a partial is not an event, it is a guess. */
  at: string | null;
}

export interface TranscriptFeed {
  finals: TranscriptLine[];
  /** The in-flight guess per speaker role. */
  partials: Record<string, TranscriptLine>;
}

/** What the transcript stream delivers per line (the `data` of the SSE frame). */
export interface TranscriptEventData {
  kind?: string;
  text?: string | null;
  seq?: number | null;
  speaker?: { role?: string; number?: string } | null;
  callId?: string;
}

export interface SpeakerLabels {
  /**
   * The NEAR leg — the one the capture was taken on, which for this phone's tap is always its
   * own owner, so the panel says "You" rather than their extension.
   */
  near: string | null;
  /** The far party as the call shows them — name if known, else number. */
  far: string | null;
}

export function emptyFeed(): TranscriptFeed {
  return { finals: [], partials: {} };
}

export function parseTranscriptEvent(data: string): TranscriptEventData | null {
  try {
    const parsed: unknown = JSON.parse(data);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as TranscriptEventData;
  } catch {
    return null;
  }
}

/** The label for a line: the call's own party info first, the wire's number as fallback. */
export function speakerLabel(
  role: string,
  number: string | null | undefined,
  labels: SpeakerLabels,
): string | null {
  // `near`/`far` name the LEG, not a role: a call can be rep to rep, where "lead" was a lie
  // about a colleague. Which person each leg is stays the caller's to resolve — here the tap is
  // this phone's own, so near is always its owner.
  if (role === "near") return labels.near ?? number ?? null;
  if (role === "far") return labels.far ?? number ?? null;
  return number ?? null; // a mono tap — no per-speaker identity to resolve
}

/**
 * Fold one stream event into the feed. Returns the same feed object when the event changes
 * nothing, so React state setters can skip a render on heartbeats and foreign lines.
 */
export function applyTranscriptEvent(
  feed: TranscriptFeed,
  event: TranscriptEventData,
  labels: SpeakerLabels,
  options: { callId: string; at: string },
): TranscriptFeed {
  // speech_start / speech_end ride the same channel; they carry no text to render.
  if (event.kind !== "partial" && event.kind !== "final") return feed;
  if (event.callId && event.callId !== options.callId) return feed;
  const text = (event.text ?? "").trim();
  if (!text) return feed;

  const role = event.speaker?.role ?? "unknown";
  const label = speakerLabel(role, event.speaker?.number, labels);

  if (event.kind === "partial") {
    return {
      finals: feed.finals,
      partials: { ...feed.partials, [role]: { key: role, role, label, text, at: null } },
    };
  }

  const partials = { ...feed.partials };
  delete partials[role];
  return {
    finals: [
      ...feed.finals,
      {
        key: `${role}-${event.seq ?? feed.finals.length}-${feed.finals.length}`,
        role,
        label,
        text,
        at: options.at,
      },
    ],
    partials,
  };
}

/** Wall-clock HH:MM:SS for a final's arrival. */
export function clockTime(now: Date = new Date()): string {
  return now.toLocaleTimeString([], { hour12: false });
}
